import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { useHashParams, useModal } from 'hooks';
import { Chip, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import BoxContainerV2 from 'components/common/BoxContainerV2';
import ResponsiveGrid from 'components/common/ResponsiveGrid';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { DetectMarketSelectors, UserSelectors } from 'store/selectors';
import Label from 'components/common/Label';
import { routes } from 'utils/routing';
import { createEcommerceItemData, GtagEventName, sendGtagEvent } from 'utils/gtag';
import { getSessionsGroups, MARKETS_SLUGS_MAP } from 'utils/transform';
import StaticMap from 'components/common/StaticMap';
import { formatDate, formatTimeDuration } from 'utils/format';
import { SpecialHost } from 'api/admin/models';
import EventAttributes from '../event/EventAttributes';
import EventV2Head from '../eventV2';
import EventDescription from '../event/EventDescription';
import EventGallery from '../event/EventGallery';
import AdditionalInstructions from '../event/AdditionalInstructions';
import HowToParticipate from '../event/HowToParticipate';
import EventExtraInfo from '../event/EventExtraInfo';
import EventContacts from '../event/EventContacts';

import { trackPage } from './ScrenTracker';
import EventActionMenu from '../eventV2/EventActionMenu';
import ImageGalleryDialog from '../event/ImageGalleryDialog';
import JLivePassportVerificationModal from '../JLivePassport/JLivePassortVerificationModal';
import EventSpecialHosts from '../event/EventSpecialHosts';
import EventFooterDescription from '../event/EventFooterDescription';

interface EventScreenV2Props {
  event: any;
  frame?: boolean;
  onToggleFavoriteEvent?: (id: number) => void;
  isPreview?: boolean;
  specialHosts?: SpecialHost[];
}

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    overflowY: 'auto',
    position: 'relative',
    maxWidth: 796,
    padding: theme.spacing(3, 0, 6.75, 10),
    overflowX: 'hidden',
    [theme.breakpoints.down('md')]: {
      maxWidth: 680,
      marginRight: 'auto',
      marginLeft: 'auto',
      padding: theme.spacing(5, 0),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 0),
    },
  },
  contentItem: {
    maxWidth: '680px',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },

  footer: {
    width: '100%',
  },

  chip: {
    backgroundColor: 'rgba(233, 235, 242, 0.5)',
    alignItems: 'flex-start', // let content grow vertically
    height: 'auto', // allow Chip to expand in height
    paddingTop: 4,
    paddingBottom: 4,
  },
  chipLabel: {
    color: '#383C44',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    display: 'block',
    lineHeight: 1.4, // add vertical spacing
    paddingTop: 2,
    paddingBottom: 2,
  },
  infoContainer: { [theme.breakpoints.down('sm')]: { paddingLeft: 0, paddingRight: 0 } },
}));

const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const isProductionEnv = () => BUILD_ENV === 'production';

const EXCLUDED_EVENTS_SCEDULED_TITLE = isProductionEnv() ? [11872, 11902, 11903, 11904, 11916, 15843] : [1917];

const EventScreenV2: React.FC<React.PropsWithChildren<EventScreenV2Props>> = ({
  event,
  frame,
  onToggleFavoriteEvent,
  isPreview = false,
  specialHosts,
}) => {
  const {
    id,
    description,
    images = [],
    videos = [],
    whats_included_tags,
    organization,
    contact_name,
    contact_phone,
    contact_phone_poc_number,
    contact_email,
    address,
    timezone,
    is_virtual,
    has_location,
    how_to_participate_non_registered,
    how_to_participate_registered,
    virtual_platform,
    livestream_link,
    type,
    sessions,
    organization_mentions = [],
    additional_instructions = [],
    target_ages,
    sponsors = [],
    eventSpecialHosts,
    settings,
    hide_footer_text,
    footer_text,
    footer_text_enabled,
  } = event;
  const { classes, cx } = useStyles();
  const [t] = useTranslation('public');
  const files = images
    .concat(Array.isArray(videos) && videos.length > 0 ? videos : [])
    .sort(({ order: aOrder }: any, { order: bOrder }: any) => aOrder - bOrder);
  const galleryFiles = files.filter(({ main }: any) => !main);
  const locationRef = useRef(null);
  const router = useRouter();
  const isSmallMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const market = useSelector(DetectMarketSelectors.getSelectedMarket);
  const [showImageGallery] = useModal(ImageGalleryDialog);
  const [showJLivePassportVerificationModal] = useModal(JLivePassportVerificationModal);
  const jLiveInternalPassport = useSelector(UserSelectors.userJLivePassport);
  const [proceedJLivePassportCreation, setProceedJLivePassportCreation] = useState(false);
  const userJLivePassportsState = useSelector(UserSelectors.userPassportsState);
  const [hashParams, setHashParams] = useHashParams();

  useEffect(() => {
    if (proceedJLivePassportCreation && userJLivePassportsState?.isFetched && !jLiveInternalPassport)
      showJLivePassportVerificationModal({
        onSuccess: () => setProceedJLivePassportCreation(true),
      });
  }, [
    jLiveInternalPassport,
    proceedJLivePassportCreation,
    showJLivePassportVerificationModal,
    userJLivePassportsState?.isFetched,
  ]);

  const handleImageClick = (file: File) => {
    const index = files.indexOf(file);
    showImageGallery({
      files,
      initialIndex: index,
    });
  };

  useEffect(() => {
    if (!isPreview) {
      trackPage('Event Page', { event_id: id });
    }
  }, [id, isPreview]);

  const goToTicketsStep = useCallback(
    (passportCreated: boolean) => {
      if (settings?.use_third_party_link && settings?.third_party_link) {
        window.open(settings.third_party_link, '_blank');
        sendGtagEvent(GtagEventName.add_to_cart, createEcommerceItemData(event));
      } else {
        router.push(routes.eventTickets(id, {}, passportCreated ? { passportCreated: true } : {}).href);
      }
    },
    [event, id, router, settings?.third_party_link, settings?.use_third_party_link],
  );

  const onJLivePassportCreated = useCallback(() => {
    // setShowJLiveCreatedBanner(true);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    goToTicketsStep(true);
  }, [goToTicketsStep]);

  useEffect(() => {
    if (hashParams.internalPassportCreated) {
      setHashParams({ ...hashParams, internalPassportCreated: undefined });
      onJLivePassportCreated();
    }
  }, [hashParams, onJLivePassportCreated, setHashParams]);

  return (
    <BoxContainerV2
      head={
        <EventV2Head
          frame={frame}
          event={event}
          onToggleFavoriteEvent={onToggleFavoriteEvent}
          hasUserInternalPassport={Boolean(jLiveInternalPassport)}
        />
      }
      frame={frame}
      overflow
      maxWidth="lg"
    >
      <ResponsiveGrid className={classes.contentContainer} container xsSpacing={5} mdSpacing={2.5}>
        <ResponsiveGrid
          className={classes.contentItem}
          item
          xs={12}
          container
          xsSpacing={3}
          mdSpacing={2.5}
          style={{ paddingTop: 0, marginLeft: 0, overflowX: 'hidden' }}
        >
          <ResponsiveGrid style={{ marginTop: 44, padding: '0 0 12px 0' }} item container xsSpacing={1} xs={12}>
            {target_ages?.map((targetAge: any) => (
              <Grid item style={{ paddingTop: 0 }}>
                <Label
                  color={isSmallMobile ? 'white' : 'grayLight'}
                  textColor={isSmallMobile ? 'textPrimary' : 'primary'}
                  size="small"
                  variant={isSmallMobile ? 'outlined' : 'contained'}
                  label={targetAge?.title}
                  hasHover
                  onClick={() =>
                    router.push(
                      routes.events({
                        target_age: targetAge?.id,
                        market_slug: MARKETS_SLUGS_MAP[market as keyof typeof MARKETS_SLUGS_MAP],
                      }).href,
                    )
                  }
                />
              </Grid>
            ))}
          </ResponsiveGrid>
          <ResponsiveGrid item xs={12} style={{ padding: '12px 0' }}>
            <EventDescription description={description} />
          </ResponsiveGrid>
          {footer_text_enabled && !hide_footer_text && footer_text && (
            <ResponsiveGrid item xs={12} style={{ padding: '12px 0' }}>
              <EventFooterDescription description={footer_text} />
            </ResponsiveGrid>
          )}
          {whats_included_tags.length > 0 && (
            <ResponsiveGrid style={{ padding: '28px 0px 14px 0' }} item xs={12}>
              <EventAttributes type={type} whatsIncludedTags={whats_included_tags} isVirtual={false} />
            </ResponsiveGrid>
          )}
          {(frame ? eventSpecialHosts.length > 0 : specialHosts.length > 0) && (
            <ResponsiveGrid style={{ padding: '26px 0px 28px 0px' }} item xs={12}>
              <EventSpecialHosts eventId={id} specialHosts={frame ? eventSpecialHosts : specialHosts} />
            </ResponsiveGrid>
          )}
          {galleryFiles.length > 0 && (
            <ResponsiveGrid item xs={12} style={{ padding: '12px 0' }}>
              <EventGallery files={galleryFiles} onImageClick={handleImageClick} />
            </ResponsiveGrid>
          )}
        </ResponsiveGrid>
        {Array.isArray(sessions) && sessions.length > 1 && (
          <ResponsiveGrid
            className={cx(classes.contentItem, classes.infoContainer)}
            style={{ paddingRight: 0 }}
            item
            container
            spacing={2}
          >
            <ResponsiveGrid item xs={12}>
              <Typography variant="h5" component="h2" paragraph>
                {t<string>('screen.event.title.sessions')}
              </Typography>
              {!EXCLUDED_EVENTS_SCEDULED_TITLE.includes(id) && (
                <Typography>{t<string>('screen.event.subtitle.schedule')}</Typography>
              )}
            </ResponsiveGrid>
            <ResponsiveGrid item container spacing={2}>
              {getSessionsGroups(sessions, timezone).map((session) => {
                const startAtDate = formatDate(new Date(session[0].start_at), 'eeee, MMMM d', timezone);
                const endAtDate = formatDate(new Date(session[0].end_at), 'eeee, MMMM d', timezone);
                const sessionDate = startAtDate === endAtDate ? startAtDate : `${startAtDate} - ${endAtDate}`;
                return (
                  <ResponsiveGrid key={session.start_at} item container spacing={2} xs={12}>
                    <ResponsiveGrid item xs={12}>
                      <Typography variant="caption" fontWeight={600}>
                        {sessionDate}
                      </Typography>
                    </ResponsiveGrid>
                    <ResponsiveGrid item xs={12}>
                      <ResponsiveGrid container spacing={1}>
                        {session.map((range: any) => {
                          const { start_at, end_at, hide_end_time } = range;
                          const time =
                            start_at && end_at && formatTimeDuration(start_at, hide_end_time ? null : end_at, timezone);
                          return (
                            <ResponsiveGrid item xs={12}>
                              <Chip
                                className={classes.chip}
                                label={
                                  <Grid container alignItems="center" spacing={2}>
                                    <Grid item>
                                      <Typography variant="body2" fontWeight={600}>{`${time.split('|')[0]} ${
                                        time.split('|')[1]
                                      }`}</Typography>
                                    </Grid>
                                    {range.title && (
                                      <Grid
                                        xs
                                        style={{
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word',
                                          overflowWrap: 'anywhere',
                                        }}
                                        item
                                      >
                                        <Typography variant="body2" fontWeight={400} textTransform="none">
                                          {range.title}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                }
                                classes={{
                                  label: classes.chipLabel,
                                }}
                              />
                            </ResponsiveGrid>
                          );
                        })}
                      </ResponsiveGrid>
                    </ResponsiveGrid>
                  </ResponsiveGrid>
                );
              })}
            </ResponsiveGrid>
          </ResponsiveGrid>
        )}
        <ResponsiveGrid className={cx(classes.contentItem, classes.infoContainer)} item container>
          {(is_virtual || livestream_link) && (
            <>
              <ResponsiveGrid item xs={12} sx={{ pb: 2 }}>
                <Typography variant="h5" component="h2">
                  {t<string>('screen.event.title.howToParticipate')}
                </Typography>
              </ResponsiveGrid>
              <ResponsiveGrid style={{ marginBottom: 40 }} item xs={12}>
                <HowToParticipate
                  howToParticipate={how_to_participate_registered || how_to_participate_non_registered}
                  virtualPlatform={virtual_platform}
                  livestream_link={livestream_link}
                />
              </ResponsiveGrid>
            </>
          )}
          {has_location && address && (
            <ResponsiveGrid item xs={12} ref={locationRef}>
              <ResponsiveGrid container>
                <ResponsiveGrid item xs={12} sx={{ pb: 3, pt: 0 }}>
                  <Typography variant="h5" component="h2">
                    {t<string>('screen.event.title.location')}
                  </Typography>
                </ResponsiveGrid>
                <ResponsiveGrid item xs={12}>
                  <StaticMap width={640} height={144} lat={address.lat?.toString()} lng={address.lng?.toString()} />
                </ResponsiveGrid>
                <ResponsiveGrid item xs={12} sx={{ pb: 2, pt: 3 }}>
                  <EventExtraInfo address={address} />
                </ResponsiveGrid>
              </ResponsiveGrid>
            </ResponsiveGrid>
          )}
          {additional_instructions.length > 0 && (
            <ResponsiveGrid item xs={12} sx={{ pb: 1, pt: 3, pl: 2.5 }}>
              <AdditionalInstructions additionalInstructions={additional_instructions} />
            </ResponsiveGrid>
          )}
          <ResponsiveGrid item xs={12} sx={{ pb: isPreview ? 7 : 2, pt: 4 }}>
            <EventContacts
              organizationMentions={organization_mentions}
              organization={organization}
              contactName={contact_name}
              contactPhone={contact_phone}
              contactPhonePocNumber={contact_phone_poc_number}
              contactEmail={contact_email}
              sponsors={sponsors}
            />
          </ResponsiveGrid>
        </ResponsiveGrid>
      </ResponsiveGrid>
      <EventActionMenu
        event={event}
        hasUserInternalPassport={Boolean(jLiveInternalPassport)}
        onJLivePassportCreated={onJLivePassportCreated}
        onSuccessfullyProceed={() => setProceedJLivePassportCreation(true)}
        passportsFetched={userJLivePassportsState?.isFetched}
        goToTicketsStep={goToTicketsStep}
      />
    </BoxContainerV2>
  );
};

export default EventScreenV2;
